// src/components/admin/ClientsManagement.jsx
import React, { useState, useEffect } from "react";
import api from "../../../services/httpService";
import "./OurClients.css";

const ClientsManagement = () => {
  // Clients State
  const [clientItems, setClientItems] = useState([]);
  const [isClientsLoading, setIsClientsLoading] = useState(true);
  const [clientsMessage, setClientsMessage] = useState("");

  // Form State
  const [activeForm, setActiveForm] = useState(null); // 'add' | 'edit' | null
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    title: "",
    description: "",
    ImageKey: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchClientItems();
  }, []);

  // ==================== FETCH ====================
  const fetchClientItems = async () => {
    setIsClientsLoading(true);
    try {
      const res = await api.get("/sentinix/clients");
      if (res.data.success) {
        const raw = res.data.data;
        const allItems = Array.isArray(raw) ? raw : raw ? [raw] : [];
        const items = allItems.filter((item) => !item.isDeleted);
        setClientItems(items);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setIsClientsLoading(false);
    }
  };

  // ==================== IMAGE HANDLER ====================
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // ==================== FORM HANDLERS ====================
  const openAddForm = () => {
    setActiveForm("add");
    setFormData({ id: "", name: "", title: "", description: "", ImageKey: "" });
    setImageFile(null);
    setImagePreview(null);
    setClientsMessage("");
  };

  const openEditForm = (item) => {
    setActiveForm("edit");
    setFormData({
      id: item.id,
      name: item.name || "",
      title: item.title || "",
      description: item.description || "",
      ImageKey: item.ImageKey || "",
    });
    setImageFile(null);
    setImagePreview(null);
    setClientsMessage("");
  };

  const closeForm = () => {
    setActiveForm(null);
    setFormData({ id: "", name: "", title: "", description: "", ImageKey: "" });
    setImageFile(null);
    setImagePreview(null);
    setClientsMessage("");
  };

  // ==================== SAVE ====================
  const handleSaveClient = async () => {
    if (!formData.name.trim() || !formData.description.trim()) {
      setClientsMessage("Name and Description are required");
      setTimeout(() => setClientsMessage(""), 3000);
      return;
    }

    setIsSaving(true);
    setClientsMessage("");

    try {
      const formDataPayload = new FormData();
      formDataPayload.append("name", formData.name);
      formDataPayload.append("title", formData.title);
      formDataPayload.append("description", formData.description);

      if (imageFile) {
        formDataPayload.append("ImageKey", imageFile);
      } else if (formData.ImageKey) {
        formDataPayload.append("ImageKey", formData.ImageKey);
      }

      let res;
      if (activeForm === "edit" && formData.id) {
        res = await api.put(
          `/sentinix/clients/${formData.id}`,
          formDataPayload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        res = await api.post(
          "/sentinix/clients",
          formDataPayload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      if (res.data.success) {
        setClientsMessage(
          activeForm === "edit"
            ? "Client updated successfully!"
            : "Client created successfully!"
        );
        fetchClientItems();
        setTimeout(() => closeForm(), 1500);
      } else {
        setClientsMessage(res.data.message || "Failed to save.");
      }
    } catch (error) {
      console.error("Error saving client:", error);
      setClientsMessage(error.response?.data?.message || "Error saving data");
    } finally {
      setIsSaving(false);
      setTimeout(() => setClientsMessage(""), 3000);
    }
  };

  // ==================== DELETE ====================
  const handleDeleteClient = async (id) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;

    try {
      const res = await api.delete(`/sentinix/clients/${id}`);
      if (res.data.success) {
        setClientsMessage("Client deleted successfully");
        fetchClientItems();
      } else {
        setClientsMessage(res.data.message || "Failed to delete");
      }
    } catch (error) {
      console.error("Error deleting client:", error);
      setClientsMessage(error.response?.data?.message || "Error deleting client");
    } finally {
      setTimeout(() => setClientsMessage(""), 3000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // ==================== RENDER ====================
  return (
    <div className="clients-mgmt-container">
      {/* Page Header */}
      <div className="clients-page-header">
        <h1>Clients Management</h1>
        <p>Manage your client listings and associated details</p>
      </div>

      {/* ==================== CLIENTS SECTION ==================== */}
      <div className="clients-card">
        <div className="clients-card-header">
          <div>
            <h2>Clients</h2>
            <p>Manage your individual client entries</p>
          </div>
          <button onClick={openAddForm} className="btn-add-client">
            + Add New Client
          </button>
        </div>

        <div className="clients-card-body">
          {clientsMessage && (
            <div
              className={`form-message ${
                clientsMessage.includes("success") ? "success" : "error"
              }`}
            >
              {clientsMessage}
            </div>
          )}

          {/* ==================== ADD / EDIT MODAL ==================== */}
          {activeForm && (
            <div className="modal-overlay" onClick={closeForm}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    {activeForm === "add" ? "Add New Client" : "Edit Client"}
                  </h3>
                  <button className="modal-close" onClick={closeForm}>
                    ✕
                  </button>
                </div>

                <div className="modal-body">
                  {clientsMessage && (
                    <div
                      className={`form-message ${
                        clientsMessage.includes("success") ? "success" : "error"
                      }`}
                    >
                      {clientsMessage}
                    </div>
                  )}

                  {/* Name */}
                  <div className="form-group-client">
                    <label>
                      Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="Enter client name"
                      className="form-input-client"
                    />
                  </div>

                  {/* Title */}
                  <div className="form-group-client">
                    <label>Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      placeholder="Enter client title"
                      className="form-input-client"
                    />
                  </div>

                  {/* Image */}
                  <div className="form-group-client">
                    <label>Image</label>
                    {(imagePreview || formData.ImageKey) && (
                      <div className="image-preview-client">
                        <img
                          src={imagePreview || formData.ImageKey}
                          alt="Client"
                        />
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="file-input-client"
                      />
                      {imageFile && (
                        <p className="image-note">
                          ✅ {imageFile.name} — will upload on Save
                        </p>
                      )}
                      {!imageFile && formData.ImageKey && (
                        <p className="image-note">
                          ℹ️ No new image selected — existing image will be kept
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="form-group-client">
                    <label>
                      Description <span className="required">*</span>
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      rows={5}
                      placeholder="Enter client description..."
                      className="form-textarea-client"
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button onClick={closeForm} className="btn-close-modal">
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveClient}
                    disabled={isSaving}
                    className="btn-save-client"
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-small"></span> Saving...
                      </>
                    ) : activeForm === "edit" ? (
                      "💾 Update Client"
                    ) : (
                      "💾 Save Client"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ==================== CLIENTS GRID ==================== */}
          {isClientsLoading ? (
            <div className="loading-box">
              <div className="spinner"></div>
              <p>Loading clients...</p>
            </div>
          ) : clientItems.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">🤝</span>
              <p>No clients added yet</p>
              <button onClick={openAddForm} className="btn-add-client">
                + Add Your First Client
              </button>
            </div>
          ) : (
            <div className="clients-grid">
              {clientItems.map((item) => (
                <div key={item.id} className="client-item-card">
                  <div className="client-item-image">
                    {item.ImageKey ? (
                      <img src={item.ImageKey} alt={item.name} />
                    ) : (
                      <div className="no-image-placeholder">
                        <span>No Image</span>
                      </div>
                    )}
                  </div>

                  <div className="client-item-info">
                    <h3>{item.name}</h3>
                    {item.title && (
                      <span className="client-title-badge">{item.title}</span>
                    )}
                    <p>{item.description}</p>
                    <span className="client-item-date">
                      {formatDate(item.createdAt)}
                    </span>
                  </div>

                  <div className="client-item-actions">
                    <button
                      onClick={() => openEditForm(item)}
                      className="btn-edit-item"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClient(item.id)}
                      className="btn-delete-item"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientsManagement;